/**
 * Pure day-key helpers for the chef temperature-log workflow.
 *
 * The temp-log task is a PER-DAY task: each calendar day gets its own
 * task id and its own progress bucket, so an unfinished Monday stays
 * open (overdue, with Monday's progress) while Tuesday starts fresh.
 * Day keys are the chef's LOCAL calendar date as `YYYY-MM-DD` — they
 * sort lexicographically in chronological order.
 *
 * Kept free of React / the `@/` path alias so `bun test` can import
 * them directly (mirrors `temp-log-progress.ts`).
 */

/** Prefix of every per-day temp-log task id (`temp-log-day-2026-06-02`). */
export const TEMP_LOG_TASK_ID_PREFIX = 'temp-log-day'

/** How long an unfinished day lingers as an overdue task before pruning. */
export const TEMP_LOG_MAX_DAY_AGE_DAYS = 7

const DAY_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/** The local calendar date of `date` as a `YYYY-MM-DD` day key. */
export function localDayKey(date: Date): string {
  const y = String(date.getFullYear()).padStart(4, '0')
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Local midnight of a day key, or null when the key is malformed.
 * Parsed by components (NOT `new Date(key)`, which reads as UTC and
 * shifts the day in western timezones).
 */
export function dateFromDayKey(dayKey: string): Date | null {
  if (!DAY_KEY_PATTERN.test(dayKey)) {
    return null
  }
  const [y, m, d] = dayKey.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  // Reject impossible dates that Date() silently rolls over (2026-02-31).
  return localDayKey(date) === dayKey ? date : null
}

/** End of a day (local 23:59) as an ISO string — the task's due moment. */
export function endOfDayIso(dayKey: string): string {
  const d = dateFromDayKey(dayKey)
  const base = d ?? new Date()
  return new Date(base.getFullYear(), base.getMonth(), base.getDate(), 23, 59, 0, 0).toISOString()
}

/** The task id for one day's temp-log task. */
export function tempLogTaskIdForDay(dayKey: string): string {
  return `${TEMP_LOG_TASK_ID_PREFIX}-${dayKey}`
}

/**
 * Recover the day key from a temp-log task id, or null when the id
 * isn't a valid per-day temp-log id. Callers fall back to today.
 */
export function dayKeyFromTempLogTaskId(taskId: string): string | null {
  const prefix = `${TEMP_LOG_TASK_ID_PREFIX}-`
  if (!taskId.startsWith(prefix)) {
    return null
  }
  const dayKey = taskId.slice(prefix.length)
  return dateFromDayKey(dayKey) ? dayKey : null
}

/**
 * Drop day entries older than `maxAgeDays` before `todayKey` (and any
 * malformed keys). Returns the same object when nothing changed so
 * store writes can be skipped.
 */
export function pruneTempLogDays<T>(
  days: Record<string, T>,
  todayKey: string,
  maxAgeDays: number = TEMP_LOG_MAX_DAY_AGE_DAYS,
): Record<string, T> {
  const today = dateFromDayKey(todayKey)
  if (!today) {
    return days
  }
  const cutoff = new Date(today.getFullYear(), today.getMonth(), today.getDate() - maxAgeDays)
  const cutoffKey = localDayKey(cutoff)
  const kept = Object.entries(days).filter(([key]) => dateFromDayKey(key) !== null && key >= cutoffKey)
  return kept.length === Object.keys(days).length ? days : Object.fromEntries(kept)
}
